import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { useFormatCurrency } from "../../hooks/useFormatCurrency";
import PrintPlatformAttribution from "./PrintPlatformAttribution";
import type { ReceiptPrintLayoutProps } from "./types";

const printSx = {
  fontFamily: '"IBM Plex Mono", "SF Mono", "Consolas", monospace',
  fontSize: "11px",
  lineHeight: 1.5,
  color: "#111",
  "@media print": {
    fontSize: "11px"
  }
} as const;

/**
 * Print-only sales receipt layout (Phase 1: mock props; Phase 2: API-backed).
 */
export const ReceiptPrintLayout: React.FC<ReceiptPrintLayoutProps> = ({
  businessName,
  addressLines,
  phone,
  documentTitle = "Sales receipt",
  dateTimeDisplay,
  receiptNumber,
  receiptNumberLabel = "Receipt #",
  cashierDisplay,
  lineItems,
  invoiceSummaryTitle = "Invoice summary",
  receiptSectionTitle = "Receipt summary",
  subtotal,
  discountLabel,
  discountAmount,
  taxLabel,
  taxAmount,
  total,
  thisPaymentAmount,
  thisPaymentLabel = "Paid on this receipt",
  paymentMethodLabel = "Payment method",
  paymentMethodDisplay,
  thankYouMessage = "Thank you for your business!",
  policyMessage
}) => {
  const formatCurrency = useFormatCurrency();

  return (
    <Box sx={printSx}>
      <Box
        sx={{
          textAlign: "center",
          borderBottom: "1px solid #000",
          pb: 1.5,
          mb: 2
        }}
      >
        <Typography
          component="div"
          sx={{
            fontSize: "18px",
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase"
          }}
        >
          {businessName}
        </Typography>
        {addressLines.map((line) => (
          <Typography
            key={line}
            component="div"
            sx={{ fontSize: "10px", color: "#333" }}
          >
            {line}
          </Typography>
        ))}
        {phone ? (
          <Typography
            component="div"
            sx={{ fontSize: "10px", color: "#333", mt: 0.25 }}
          >
            {phone}
          </Typography>
        ) : null}
        <Typography
          component="div"
          sx={{
            fontSize: "13px",
            fontWeight: 700,
            mt: 1.5,
            textTransform: "uppercase"
          }}
        >
          {documentTitle}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 1,
          mb: 2,
          fontSize: "10px"
        }}
      >
        <Box>
          <Typography component="div" sx={{ color: "#555" }}>
            {receiptNumberLabel}
          </Typography>
          <Typography component="div" sx={{ fontWeight: 700 }}>
            {receiptNumber}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography component="div" sx={{ color: "#555" }}>
            Date &amp; time
          </Typography>
          <Typography component="div" sx={{ fontWeight: 600 }}>
            {dateTimeDisplay}
          </Typography>
        </Box>
        <Box sx={{ gridColumn: "1 / -1" }}>
          <Typography component="div" sx={{ color: "#555" }}>
            Cashier
          </Typography>
          <Typography component="div">{cashierDisplay}</Typography>
        </Box>
      </Box>

      <Table
        size="small"
        sx={{
          width: "100%",
          fontSize: "10px",
          mb: 1.5,
          "& .MuiTableCell-root": { borderColor: "#ddd", py: 0.75, px: 0.5 }
        }}
      >
        <TableHead>
          <TableRow sx={{ borderBottom: "1px solid #000" }}>
            <TableCell
              sx={{
                textAlign: "left",
                fontWeight: 700,
                borderBottomColor: "#000"
              }}
            >
              Item
            </TableCell>
            <TableCell
              sx={{
                textAlign: "right",
                fontWeight: 700,
                width: "2.5rem",
                borderBottomColor: "#000"
              }}
            >
              Qty
            </TableCell>
            <TableCell
              sx={{
                textAlign: "right",
                fontWeight: 700,
                width: "4.5rem",
                borderBottomColor: "#000"
              }}
            >
              Each
            </TableCell>
            <TableCell
              sx={{
                textAlign: "right",
                fontWeight: 700,
                width: "5rem",
                borderBottomColor: "#000"
              }}
            >
              Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lineItems.map((row) => (
            <TableRow key={row.name}>
              <TableCell sx={{ verticalAlign: "top", pr: 1 }}>
                {row.name}
              </TableCell>
              <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                {row.quantity}
              </TableCell>
              <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                {formatCurrency(row.unitPrice)}
              </TableCell>
              <TableCell
                sx={{
                  textAlign: "right",
                  whiteSpace: "nowrap",
                  fontWeight: 600
                }}
              >
                {formatCurrency(row.lineTotal)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2,
          columnGap: 3,
          fontSize: "10px",
          width: "100%",
          mt: 1,
          pt: "10px",
          alignItems: "start"
        }}
      >
        {/* Left: receipt summary — amount paid on this receipt only */}
        <Box
          sx={{
            pr: 1,
            borderRight: "1px solid #ddd",
            minWidth: 0
          }}
        >
          <Typography
            component="div"
            sx={{
              fontWeight: 700,
              fontSize: "11px",
              mb: 0.75,
              borderBottom: "1px solid #ccc",
              pb: 0.5
            }}
          >
            {receiptSectionTitle}
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              py: 0.35,
              gap: 1,
              fontWeight: 700
            }}
          >
            <Typography component="span" sx={{ minWidth: 0, pr: 0.5 }}>
              {thisPaymentLabel}
            </Typography>
            <Typography
              component="span"
              sx={{ flexShrink: 0, fontWeight: 700, color: "#2e7d32" }}
            >
              {formatCurrency(thisPaymentAmount)}
            </Typography>
          </Box>
        </Box>

        {/* Right: invoice sale totals */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            minWidth: 0,
            pl: 0.5
          }}
        >
          <Box sx={{ width: "100%", maxWidth: "240px" }}>
            {invoiceSummaryTitle ? (
              <Typography
                component="div"
                sx={{
                  fontWeight: 700,
                  fontSize: "11px",
                  mb: 0.75,
                  borderBottom: "1px solid #ccc",
                  pb: 0.5
                }}
              >
                {invoiceSummaryTitle}
              </Typography>
            ) : null}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                py: 0.35
              }}
            >
              <Typography component="span">Subtotal</Typography>
              <Typography component="span">
                {formatCurrency(subtotal)}
              </Typography>
            </Box>
            {discountLabel != null && discountAmount != null ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  py: 0.35
                }}
              >
                <Typography component="span">{discountLabel}</Typography>
                <Typography component="span">
                  {formatCurrency(discountAmount)}
                </Typography>
              </Box>
            ) : null}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                py: 0.35
              }}
            >
              <Typography component="span">{taxLabel}</Typography>
              <Typography component="span">
                {formatCurrency(taxAmount)}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                py: 0.75,
                mt: 0.5,
                borderTop: "2px solid #000",
                fontWeight: 700,
                fontSize: "12px"
              }}
            >
              <Typography component="span">Total</Typography>
              <Typography component="span">{formatCurrency(total)}</Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{ mt: 2, pt: 1.5, borderTop: "1px dashed #999", fontSize: "10px" }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography component="span" sx={{ color: "#555" }}>
            {paymentMethodLabel}
          </Typography>
          <Typography component="span" sx={{ fontWeight: 600 }}>
            {paymentMethodDisplay}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ textAlign: "center", mt: 3 }}>
        <Typography
          component="div"
          sx={{ fontStyle: "italic", fontSize: "10px" }}
        >
          {thankYouMessage}
        </Typography>
        {policyMessage ? (
          <Typography
            component="div"
            sx={{
              fontSize: "9px",
              color: "#555",
              mt: 1,
              maxWidth: "72ch",
              mx: "auto"
            }}
          >
            {policyMessage}
          </Typography>
        ) : null}
        <PrintPlatformAttribution />
      </Box>
    </Box>
  );
};

export default ReceiptPrintLayout;
